/**
 * SMSWiser prepared drops flex mutations (country-level, mixed campaigns) + bulk send stream
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import api, { clearAuthAndRedirect, getCorrelationId } from '@/services/api';

import { keys } from '../keys';
import { invalidatePreparedDrops } from '../invalidate-prepared-drops';
import type { BulkUpdatePreparedDropsPayload, UpdatePreparedDropPayload } from './mutations-prepared-drops';
import { unwrapApiData } from '../utils';

export type BulkSendEvent =
  | { preparedDropId: string; status: 'sent'; dropId: number; uniqueId: string; title: string }
  | { preparedDropId: string; status: 'error'; message: string }
  | { status: 'done'; total: number; sent: number; failed: number };

export type BulkDraftEvent =
  | {
      preparedDropId: string;
      status: 'created';
      dropId: number;
      uniqueId: string;
      title: string;
      alreadyLinked: boolean;
    }
  | { preparedDropId: string; status: 'error'; message: string }
  | { status: 'done'; total: number; created: number; failed: number };

export interface SseStreamOptions<T> {
  getAuthToken: () => string | null;
  getApiBase: () => string;
  onEvent: (event: T) => void;
}

/** Update/delete/send with per-drop wiserId and explicit countryKey (supports merged pinned list). */
export function useUpdatePreparedDropFlex() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      countryKey,
      id,
      payload,
      wiserId,
    }: { countryKey: string; id: string; payload: UpdatePreparedDropPayload; wiserId: number }) => {
      const res = await api.patch(
        `/smswiser/campaigns/${encodeURIComponent(countryKey)}/${wiserId}/prepared-drops/${id}`,
        payload,
      );
      return unwrapApiData<PreparedDrop>(res.data);
    },
    onSuccess: (_data, vars) => {
      invalidatePreparedDrops(qc, vars.countryKey);
    },
  });
}

export function useDeletePreparedDropFlex() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      countryKey,
      id,
      wiserId,
    }: { countryKey: string; id: string; wiserId: number }) => {
      await api.delete(
        `/smswiser/campaigns/${encodeURIComponent(countryKey)}/${wiserId}/prepared-drops/${id}`,
      );
    },
    onSuccess: (_void, vars) => {
      invalidatePreparedDrops(qc, vars.countryKey);
    },
  });
}

/** Bulk delete prepared drops (single request per country). */
export function useBulkDeletePreparedDropsFlex() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      countryKey,
      ids,
      undoGroupId,
    }: {
      countryKey: string;
      ids: string[];
      /** One id per user CLICK — a delete spanning several countries is one
       *  request each, and this is what makes them undo as a single step. */
      undoGroupId?: string;
    }) => {
      const res = await api.delete(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/prepared-drops/bulk`,
        { data: { ids, ...(undoGroupId ? { undoGroupId } : {}) } },
      );
      return unwrapApiData<{ deleted: number }>(res.data);
    },
    onSuccess: (_data, vars) => {
      invalidatePreparedDrops(qc, vars.countryKey);
    },
  });
}

export function useSendPreparedDropFlex() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      countryKey,
      preparedDropId,
      wiserId,
    }: { countryKey: string; preparedDropId: string; wiserId: number }) => {
      const res = await api.post(
        `/smswiser/campaigns/${encodeURIComponent(countryKey)}/${wiserId}/prepared-drops/${preparedDropId}/create-drop`,
      );
      return unwrapApiData<{ ok: true; title: string; uniqueId: string; dropId?: number }>(res.data);
    },
    onSuccess: (_data, vars) => {
      invalidatePreparedDrops(qc, vars.countryKey);
      qc.invalidateQueries({ queryKey: keys.dropsWithStatsPrefix(vars.countryKey) });
    },
  });
}

/** Reorder prepared drops (country-level, supports workDate scope). */
export function useReorderPreparedDropsFlex() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      countryKey,
      workDate,
      updates,
    }: {
      countryKey: string;
      workDate?: string;
      updates: Array<{ id: string; orderIndex: number }>;
    }) => {
      const res = await api.patch(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/prepared-drops/reorder`,
        { workDate, updates },
      );
      return unwrapApiData<{ updated: number }>(res.data);
    },
    onMutate: async (variables) => {
      const { countryKey, workDate, updates } = variables;
      const queryKey = keys.preparedDropsByCountry(countryKey, workDate);
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<PreparedDrop[]>(queryKey);
      if (!prev) return { prev: undefined, queryKey };
      const orderMap = new Map(updates.map((u) => [u.id, u.orderIndex]));
      const reordered = [...prev]
        .map((d) => ({ ...d, orderIndex: orderMap.get(d.id) ?? d.orderIndex ?? 0 }))
        .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
      qc.setQueryData(queryKey, reordered);
      return { prev, queryKey };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev != null && ctx?.queryKey) {
        qc.setQueryData(ctx.queryKey, ctx.prev);
      }
    },
    onSuccess: (_data, vars) => {
      invalidatePreparedDrops(qc, vars.countryKey);
    },
  });
}

/** Bulk update when drops span multiple campaigns (within one country). */
export function useBulkUpdatePreparedDropsFlex() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      countryKey,
      items,
      payload,
    }: {
      countryKey: string;
      items: Array<{ id: string; wiserId: number }>;
      payload: BulkUpdatePreparedDropsPayload;
    }) => {
      const ids = items.map((i) => i.id);
      if (payload.wiserId != null && payload.wiserId > 0) {
        const res = await api.patch(
          `/smswiser/countries/${encodeURIComponent(countryKey)}/prepared-drops/bulk`,
          { ids, payload },
        );
        const data = unwrapApiData<{ updated: number }>(res.data);
        return { updated: data.updated ?? 0 };
      }
      const byWiserId = new Map<number, string[]>();
      for (const { id, wiserId } of items) {
        const list = byWiserId.get(wiserId) ?? [];
        list.push(id);
        byWiserId.set(wiserId, list);
      }
      let total = 0;
      for (const [wiserId, groupIds] of byWiserId) {
        const { wiserId: _w, ...rest } = payload;
        const res = await api.patch(
          `/smswiser/campaigns/${encodeURIComponent(countryKey)}/${wiserId}/prepared-drops/bulk`,
          { ids: groupIds, payload: rest },
        );
        const data = unwrapApiData<{ updated: number }>(res.data);
        total += data.updated ?? 0;
      }
      return { updated: total };
    },
    onSuccess: (_data, vars) => {
      invalidatePreparedDrops(qc, vars.countryKey);
    },
  });
}

/** POST `ids` to an SSE endpoint and invoke onEvent for each `data:` line. */
async function postSseStream<T>(
  url: string,
  ids: string[],
  options: SseStreamOptions<T>,
): Promise<void> {
  const token = options.getAuthToken();
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Correlation-Id': getCorrelationId(),
    },
    body: JSON.stringify({ ids }),
  });
  if (res.status === 401) {
    clearAuthAndRedirect();
    throw new Error('Session expired. Please log in again.');
  }
  if (!res.ok) throw new Error(res.statusText);
  const reader = res.body?.getReader();
  if (!reader) throw new Error('No response body');
  const decoder = new TextDecoder();
  let buffer = '';
  const emit = (line: string) => {
    if (!line.startsWith('data: ')) return;
    try {
      options.onEvent(JSON.parse(line.slice(6)) as T);
    } catch {
      // ignore parse errors
    }
  };
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) emit(line);
  }
  emit(buffer);
}

/** Bulk send prepared drops via POST + SSE stream. Calls onEvent for each SSE event. */
export function bulkSendPreparedDropsStream(
  countryKey: string,
  wiserId: number,
  ids: string[],
  options: SseStreamOptions<BulkSendEvent>,
): Promise<void> {
  const base = options.getApiBase().replace(/\/$/, '');
  const url = `${base}/api/smswiser/campaigns/${encodeURIComponent(countryKey)}/${wiserId}/prepared-drops/bulk-send`;
  return postSseStream<BulkSendEvent>(url, ids, options);
}

/** Bulk-create SMSWiser drafts via POST + SSE stream. Calls onEvent for each SSE event. */
export function bulkCreateSmswiserDraftsStream(
  countryKey: string,
  wiserId: number,
  ids: string[],
  options: SseStreamOptions<BulkDraftEvent>,
): Promise<void> {
  const base = options.getApiBase().replace(/\/$/, '');
  const url = `${base}/api/smswiser/campaigns/${encodeURIComponent(countryKey)}/${wiserId}/prepared-drops/bulk-smswiser-draft`;
  return postSseStream<BulkDraftEvent>(url, ids, options);
}
